/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ThreeEvent, useThree } from '@react-three/fiber';
import React, { Ref, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import type { NormalBufferAttributes as NBAs, Object3D } from 'three';
import * as THREE from 'three';
import { BufferGeometry, Mesh, Vector3 } from 'three';

import { EInteractionMode, EWall, EWallMode, EWindingOrder } from '../../@types/Enums';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { useWallManagement } from '../../hooks/useWallManagement';
import { IWall } from '../../models/abstract/Wall';
import ElementModel, { IElement } from '../../models/standard/ElementModel';
import PartitionWallModel from '../../models/standard/PartitionWallModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../models/standard/PhysicalBoundary';

import { useWallActions } from '../../provider/BuildingProvider/actions/useWallActions';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useConfirmationDialog } from '../../provider/ConfirmationDialogProvider';
import { useDrawing } from '../../provider/DrawingProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { useValidation } from '../../provider/ValidationProvider';

import { fetchAttributesForObject, updateWallGeometryAttributes } from '../../utils/attributes/attributesForObjectLoader';
import { ObjectType } from '../../utils/attributes/defaultObjectsAndAttributes';
import { MM_TO_INTERNAL_FACTOR, SINGLE_CANVAS_SIDE_SIZE } from '../../utils/constants';

import { EPendingType, IPendingModel2DData, IPendingProductData } from '../../@types/PendingItemTypes';
import AttributeModel from '../../models/standard/AttributeModel';

import FloorModel from '../../models/standard/FloorModel';
import RoomModel from '../../models/standard/RoomModel';
import SpaceModel from '../../models/standard/SpaceModel';
import StandardWallModel, { IStandardWall } from '../../models/standard/StandardWallModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { usePendingItem } from '../../provider/PendingItemProvider';
import { usePlan } from '../../provider/PlanProvider';
import { getItemFootprint as getFootprintFromRegistry } from '../../utils/itemFootprintRegistry';
import { findPartitionSplitTargetInBuilding } from '../../utils/multiroomUtils';
import { boxGrey, immersightBlue } from '../../utils/colors';
import { getSpaceUuidAtPointByGeometry } from '../../utils/spaceUtils';
import { calculateWindingOrder, reorderWindingOrderToClockwise } from '../../utils/vectorUtils';
import { DockingPointMesh } from './DockingPointMesh';
import Element from './Element';
import Model2D from './Model2D';
import PartitionWall from './PartitionWall';
import PreviewWall from './PreviewWall';
import Product from './Product';
import Room from './Room';
import SnapLine from './SnapLine';
import StandardWall from './StandardWall';

// --- GEOMETRY HELPERS ---
const EPS = 1e-6;
function pointToSegmentInfo(A: THREE.Vector3, B: THREE.Vector3, P: THREE.Vector3) {
  const AB = B.clone().sub(A);
  const len2 = Math.max(EPS, AB.lengthSq());
  const t = Math.max(0, Math.min(1, P.clone().sub(A).dot(AB) / len2));
  const Q = A.clone().addScaledVector(AB, t);
  const dist = P.distanceTo(Q);
  return { t, Q, dist };
}
function projectPointToWallCenterline(wall: IStandardWall, point: THREE.Vector3): THREE.Vector3 {
  const { Q } = pointToSegmentInfo(wall.startPoint, wall.endPoint, point);
  Q.z = 0;
  return Q;
}

// Global Lock
let globalClickLock = false;

// Reusable Vectors for Highlight Logic
const TEMP_ROT = new THREE.Euler(0, 0, 0);
const FLOOR_HIGHLIGHT_SOURCE_COLOR = new THREE.Color(boxGrey).getHex();
const FLOOR_HIGHLIGHT_TARGET_COLOR = new THREE.Color(immersightBlue).getHex();

// Type for drag handler
type ItemType = 'element' | 'product' | 'model2D';
type FloorTarget = {
  mesh: THREE.Mesh;
  box: THREE.Box2;
};
type FloorCandidate = FloorTarget & {
  overlapArea: number;
};
type HighlightEntry = {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  colorHex: number;
  originalColor: THREE.Color;
  originalMap: THREE.Texture | null;
  originalTransparent: boolean;
  originalOpacity: number;
  originalDepthWrite: boolean;
};

const restoreFloorHighlight = (entry: HighlightEntry) => {
  const { material } = entry;

  if (entry.mesh.material !== material) return;

  material.color.copy(entry.originalColor);
  material.map = entry.originalMap;
  material.transparent = entry.originalTransparent;
  material.opacity = entry.originalOpacity;
  material.depthWrite = entry.originalDepthWrite;
  material.needsUpdate = true;
};

const applyFloorHighlight = (mesh: THREE.Mesh, colorHex: number): HighlightEntry | null => {
  if (!(mesh.material instanceof THREE.MeshBasicMaterial)) return null;

  const material = mesh.material;
  const entry: HighlightEntry = {
    mesh,
    material,
    colorHex,
    originalColor: material.color.clone(),
    originalMap: material.map,
    originalTransparent: material.transparent,
    originalOpacity: material.opacity,
    originalDepthWrite: material.depthWrite,
  };

  material.map = null;
  material.color.setHex(colorHex);
  material.transparent = false;
  material.opacity = 1;
  material.depthWrite = true;
  material.needsUpdate = true;

  return entry;
};

const areHighlightTargetsEqual = (current: HighlightEntry[], desired: Array<{ mesh: THREE.Mesh; colorHex: number }>) => {
  if (current.length !== desired.length) return false;

  return desired.every((target, index) => {
    const entry = current[index];

    if (!entry || entry.mesh !== target.mesh || entry.colorHex !== target.colorHex) return false;
    if (entry.mesh.material !== entry.material) return false;

    return entry.material.color.getHex() === target.colorHex && entry.material.map === null && entry.material.transparent === false && entry.material.opacity === 1;
  });
};

const boxesOverlap2D = (a: THREE.Box2, b: THREE.Box2) => !(a.max.x < b.min.x || b.max.x < a.min.x || a.max.y < b.min.y || b.max.y < a.min.y);

const boxOverlapArea2D = (a: THREE.Box2, b: THREE.Box2) => {
  const width = Math.max(0, Math.min(a.max.x, b.max.x) - Math.max(a.min.x, b.min.x));
  const height = Math.max(0, Math.min(a.max.y, b.max.y) - Math.max(a.min.y, b.min.y));

  return width * height;
};

const FOOTPRINT_SAMPLE_FACTORS: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

const Building: React.FC = () => {
  const { camera, raycaster, scene } = useThree();
  const planeRef = useRef<Mesh<BufferGeometry<NBAs>>>();
  const intl = useIntl();
  const { isDrawingAllowed, setIsDrawingAllowed, wallMode, setWallMode, interactionMode, setInteractionMode } = useToolbarContext();
  const drawEnabled = interactionMode === EInteractionMode.DRAW;
  const intlRef = useRef<IntlShape | null>(null);

  useEffect(() => {
    intlRef.current = intl;
  }, [intl]);

  // Use FLATTENED lists: allProducts, allModels2D, allElements
  const { standardWalls, partitionWalls, setBuildingWalls, building, allElements, allProducts, allModels2D, setBuilding, updateElement, updateProduct, updateModel2D } =
    useBuilding();
  const LINK_EPS = 1e-3;

  const near = (a: Vector3, b: Vector3, eps = LINK_EPS) => a.distanceTo(b) <= eps;
  type OrderedEdge = { wall: IStandardWall; reversed: boolean };

  const EPS_CHAIN = 0.05;
  const isSamePt = (a: THREE.Vector3, b: THREE.Vector3, eps = EPS_CHAIN) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

  function buildContinuousEdges(loopWalls: IStandardWall[]): OrderedEdge[] {
    const remaining = loopWalls.slice();
    if (remaining.length === 0) return [];

    const ordered: OrderedEdge[] = [];

    // start with first wall as-is
    const first = remaining.shift()!;
    ordered.push({ wall: first, reversed: false });

    // cursor = current chain end
    let cursorly = first.endPoint.clone();

    // greedily chain
    while (remaining.length > 0) {
      let foundIndex = -1;
      let foundReversed = false;

      for (let i = 0; i < remaining.length; i++) {
        const w = remaining[i];
        if (isSamePt(cursorly, w.startPoint)) {
          foundIndex = i;
          foundReversed = false;
          break;
        }
        if (isSamePt(cursorly, w.endPoint)) {
          foundIndex = i;
          foundReversed = true;
          break;
        }
      }

      if (foundIndex === -1) break;

      const [picked] = remaining.splice(foundIndex, 1);
      ordered.push({ wall: picked, reversed: foundReversed });

      cursorly = foundReversed ? picked.startPoint.clone() : picked.endPoint.clone();
    }

    return ordered;
  }

  // optional: weld tiny numeric gaps so boundary isn’t “broken”
  function weldOrderedChain(ordered: OrderedEdge[], eps = EPS_CHAIN) {
    if (ordered.length < 2) return;

    const endOf = (e: OrderedEdge) => (e.reversed ? e.wall.startPoint : e.wall.endPoint);
    const startOf = (e: OrderedEdge) => (e.reversed ? e.wall.endPoint : e.wall.startPoint);

    for (let i = 0; i < ordered.length; i++) {
      const a = ordered[i];
      const b = ordered[(i + 1) % ordered.length];

      const aEnd = endOf(a);
      const bStart = startOf(b);

      if (!isSamePt(aEnd, bStart, eps)) continue;

      // choose one shared point (keep aEnd), snap the other onto it
      if (b.reversed) b.wall.endPoint.copy(aEnd);
      else b.wall.startPoint.copy(aEnd);
    }
  }
  function isOrderedChainClosed(ordered: OrderedEdge[], eps = EPS_CHAIN) {
    if (ordered.length < 3) return false;

    const endOf = (e: OrderedEdge) => (e.reversed ? e.wall.startPoint : e.wall.endPoint);
    const startOf = (e: OrderedEdge) => (e.reversed ? e.wall.endPoint : e.wall.startPoint);

    return isSamePt(endOf(ordered[ordered.length - 1]), startOf(ordered[0]), eps);
  }

  function isPointOnWallSegment(point: Vector3, wall: IStandardWall, eps = EPS_CHAIN) {
    const ab = wall.endPoint.clone().sub(wall.startPoint);
    const ap = point.clone().sub(wall.startPoint);
    const lenSq = ab.lengthSq();

    if (lenSq <= eps * eps) return false;
    if (Math.abs(ab.x * ap.y - ab.y * ap.x) > eps * Math.sqrt(lenSq)) return false;

    const t = ap.dot(ab);

    return t >= -eps && t <= lenSq + eps;
  }

  function areWallsColinear(a: IStandardWall, b: IStandardWall, eps = EPS_CHAIN) {
    const ad = a.endPoint.clone().sub(a.startPoint);
    const bd = b.endPoint.clone().sub(b.startPoint);
    const al = ad.length();
    const bl = bd.length();

    if (al <= eps || bl <= eps) return false;

    return Math.abs(ad.x * bd.y - ad.y * bd.x) <= eps * al * bl;
  }

  function isFoldedIntoPreviousWall(previous: IStandardWall, current: IStandardWall) {
    if (!areWallsColinear(previous, current)) return false;

    return (
      (isSamePt(previous.endPoint, current.endPoint) && isPointOnWallSegment(current.startPoint, previous)) ||
      (isSamePt(previous.startPoint, current.startPoint) && isPointOnWallSegment(current.endPoint, previous))
    );
  }

  function removeFoldedLoopStubs(loopWalls: IStandardWall[], protectedWallUuids: Set<string>) {
    const kept: IStandardWall[] = [];
    const removedUuids = new Set<string>();

    loopWalls.forEach((wall) => {
      const previous = kept[kept.length - 1];

      if (previous && !protectedWallUuids.has(wall.uuid) && isFoldedIntoPreviousWall(previous, wall)) {
        removedUuids.add(wall.uuid);
        return;
      }

      kept.push(wall);
    });

    return { walls: kept, removedUuids };
  }

  const handleLoopClosed = useCallback(
    (loopWalls: IStandardWall[]) => {
      if (!Array.isArray(loopWalls) || loopWalls.length < 3) return;

      setBuilding((prev) => {
        const prevWalls = Array.isArray(prev.walls) ? prev.walls : [];
        const rooms = Array.isArray(prev.rooms) ? prev.rooms.slice() : [];

        const floor = new FloorModel(undefined, null, [], undefined);
        const loopUuids = new Set(loopWalls.map((w) => w.uuid));
        const windingOrder = calculateWindingOrder(loopWalls);

        let normalizedLoopWalls = loopWalls.slice();
        let nextBuildingWalls = prevWalls;

        /**
         * IMPORTANT:
         * useWallManagement may call onLoopClosed immediately after setBuildingWalls.
         * React state might not contain those new walls yet, so make sure they exist
         * in this same transaction.
         */
        const existingWallUuids = new Set(prevWalls.filter(Boolean).map((w: any) => w.uuid));
        const missingLoopWalls = loopWalls.filter((w) => w && !existingWallUuids.has(w.uuid));

        if (missingLoopWalls.length > 0) {
          nextBuildingWalls = [...prevWalls, ...missingLoopWalls];
        }

        let hasSharedWalls = false;
        const sharedWallUuids = new Set<string>();

        (prev as any).rooms?.forEach((r: any) => {
          r.spaces?.forEach((s: any) => {
            s.walls?.forEach((sw: any) => {
              if (loopUuids.has(sw.uuid)) {
                hasSharedWalls = true;
                sharedWallUuids.add(sw.uuid);
              }
            });
          });
        });

        if (windingOrder === EWindingOrder.COUNTERCLOCKWISE) {
          if (hasSharedWalls) {
            normalizedLoopWalls.reverse();

            const origByUuid = new Map<string, any>(nextBuildingWalls.filter(Boolean).map((w: any) => [w.uuid, w]));

            normalizedLoopWalls.forEach((w) => {
              if (!sharedWallUuids.has(w.uuid)) {
                const orig = origByUuid.get(w.uuid);

                if (orig) {
                  const temp = orig.startPoint.clone();
                  orig.startPoint = orig.endPoint.clone();
                  orig.endPoint = temp;

                  if (typeof orig.updateWallLengths === 'function') {
                    orig.updateWallLengths();
                  }
                }
              }
            });

            normalizedLoopWalls = normalizedLoopWalls.map((w) => origByUuid.get(w.uuid) ?? w);
          } else {
            const reordered = reorderWindingOrderToClockwise(loopWalls) as IStandardWall[];
            const origByUuid = new Map<string, any>(nextBuildingWalls.filter(Boolean).map((w: any) => [w.uuid, w]));

            reordered.forEach((computed) => {
              const orig = origByUuid.get(computed.uuid);
              if (!orig) return;

              orig.startPoint = computed.startPoint.clone();
              orig.endPoint = computed.endPoint.clone();

              if (typeof orig.updateWallLengths === 'function') {
                orig.updateWallLengths();
              }
            });

            normalizedLoopWalls = reordered.map((w) => origByUuid.get(w.uuid) ?? w);
            nextBuildingWalls = nextBuildingWalls.slice();
          }
        } else {
          const byUuid = new Map(nextBuildingWalls.filter(Boolean).map((w: any) => [w.uuid, w]));
          normalizedLoopWalls = loopWalls.map((w) => (byUuid.get(w.uuid) as any) ?? w);
        }

        const foldedStubResult = removeFoldedLoopStubs(normalizedLoopWalls, sharedWallUuids);
        normalizedLoopWalls = foldedStubResult.walls;

        if (foldedStubResult.removedUuids.size > 0) {
          nextBuildingWalls = nextBuildingWalls.filter((wall: any) => !foldedStubResult.removedUuids.has(wall?.uuid));
        }

        const ordered = buildContinuousEdges(normalizedLoopWalls);

        if (ordered.length !== normalizedLoopWalls.length || !isOrderedChainClosed(ordered)) {
          console.warn('[handleLoopClosed] could not build continuous loop edges');
          return prev;
        }

        weldOrderedChain(ordered);

        const usedSides = new Map<string, Set<'A' | 'B'>>();

        (prev.rooms ?? []).forEach((r: any) => {
          (r.spaces ?? []).forEach((s: any) => {
            (s.boundaries ?? []).forEach((b: any) => {
              if (b instanceof PhysicalBoundaryModel && Array.isArray(b.edges)) {
                b.edges.forEach((e: any) => {
                  if (e?.wall?.uuid && e.side) {
                    if (!usedSides.has(e.wall.uuid)) usedSides.set(e.wall.uuid, new Set());
                    usedSides.get(e.wall.uuid)!.add(e.side);
                  }
                });
              }
            });
          });
        });

        const boundary = new PhysicalBoundaryModel(normalizedLoopWalls as any);

        const edges = ordered.map(({ wall, reversed }) => {
          const t0 = reversed ? 1 : 0;
          const t1 = reversed ? 0 : 1;

          let assignedSide: 'A' | 'B' = 'A';
          const usage = usedSides.get(wall.uuid);

          if (usage && usage.has('A') && !usage.has('B')) {
            assignedSide = 'B';
          }

          return new BoundaryEdge(wall, t0, t1, assignedSide, reversed);
        });

        boundary.setEdges(edges);

        const space = new SpaceModel(floor, [], [], [], [boundary], normalizedLoopWalls as any);

        const intlNow = intlRef.current;
        const title = intlNow ? intlNow.formatMessage({ id: 'Room.title', defaultMessage: 'Room {number}' }, { number: rooms.length + 1 }) : `Room ${rooms.length + 1}`;

        const room = new RoomModel(title, [space], []);

        rooms.push(room);

        return {
          ...prev,
          walls: nextBuildingWalls,
          rooms,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.ROOM_CREATED,
        };
      });

      setIsDrawingAllowed(false);
      setInteractionMode(EInteractionMode.NONE);
    },
    [setBuilding, setInteractionMode, setIsDrawingAllowed, intl],
  );
  const { isDrawing, roomCompleted, setRoomCompleted, setIsDrawing } = useDrawing();
  const { tempWall, previewWalls, snapLines, createWall, updateWall, cancelAndCleanup } = useWallManagement(
    drawEnabled,
    isDrawing,
    setIsDrawing,
    roomCompleted,
    setRoomCompleted,
    handleLoopClosed,
  );

  const { hasIntersectingWalls, setHasIntersectingWalls } = useValidation();
  const { showConfirmationDialog, closeConfirmationDialog } = useConfirmationDialog();
  const { unitType } = useUnitContext();
  const { finalizePartitionWallIfSplitsRoomByUuid } = useWallActions(setBuilding);

  const { pendingItem, cancelPlacement } = usePendingItem();
  const [ghostPosition, setGhostPosition] = useState<Vector3 | null>(null);
  useEffect(() => {
    if (!drawEnabled) cancelAndCleanup();
  }, [drawEnabled, cancelAndCleanup]);
  const spriteKey = (spr: any) => spr?.uuid ?? `id:${spr?.id ?? ''}`;

  // spaceUuid -> roomUuid (valid only for this building snapshot)
  const buildSpaceToRoomIndex = (building: any): Map<string, string> => {
    const m = new Map<string, string>();
    for (const room of building?.rooms ?? []) {
      if (!room?.uuid) continue;
      for (const space of room?.spaces ?? []) {
        if (space?.uuid) m.set(space.uuid, room.uuid);
      }
    }
    return m;
  };

  // collect sprite keys that belong to a given room (by geometry)
  const collectSpriteKeysInRoom = (building: any, sprites: any[], roomUuid: string, getSpaceUuidAtPointByGeometry: (b: any, p: THREE.Vector3) => string | null): Set<string> => {
    const spaceToRoom = buildSpaceToRoomIndex(building);
    const keys = new Set<string>();

    for (const spr of sprites ?? []) {
      const pos = spr?.position as THREE.Vector3 | undefined;
      if (!pos) continue;

      const spaceUuid = getSpaceUuidAtPointByGeometry(building, pos);
      if (!spaceUuid) continue;

      if (spaceToRoom.get(spaceUuid) === roomUuid) {
        keys.add(spriteKey(spr));
      }
    }
    return keys;
  };

  // --- GHOST MODEL CALCULATIONS ---
  const ghostElement = useMemo(() => {
    if (!pendingItem || pendingItem.type !== EPendingType.ELEMENT) return null;
    const { type, length, width, rotation, sideC, triangleAngle } = pendingItem.data as any;
    return new ElementModel(
      type,
      length,
      width,
      new Vector3(0, 0, 0), // Position (updated by render loop)
      rotation,
      null, // texture
      undefined, // description
      false, // useAsTextField
      undefined, // fontSize
      undefined, // object
      [], // attributes
      sideC, // sideC
      triangleAngle,
    );
  }, [pendingItem]);

  const dockingPoints = useMemo(() => {
    // Zeige Punkte nur im Haupt-Zeichenmodus
    if (!drawEnabled) return [];

    const points: THREE.Vector3[] = [];
    const seen = new Set<string>();

    standardWalls.forEach((w) => {
      if (!w || !(w instanceof StandardWallModel)) return;

      [w.startPoint, w.endPoint].forEach((p) => {
        if (!p) return;
        const key = `${Math.round(p.x * 100)}_${Math.round(p.y * 100)}`;
        if (!seen.has(key)) {
          seen.add(key);
          points.push(p);
        }
      });
    });
    return points;
  }, [drawEnabled, standardWalls]);

  const ghostProduct = useMemo(() => {
    if (!pendingItem || pendingItem.type !== EPendingType.PRODUCT) return null;
    const data = pendingItem.data as IPendingProductData;
    // Return a dummy object matching the props expected by <Product />
    return {
      ...data.productModel,
      uuid: 'ghost-product',
      position: new Vector3(0, 0, 0),
      // FIX: Use the actual rotation from the fetched model
      rotation: data.productModel.rotation ?? 0,
      // Ensure dimensions exist
      dimensions: {
        length: data.productModel.dimensions.length ?? 0,
        depth: data.productModel.dimensions.depth ?? 0,
        height: 0,
      },
    };
  }, [pendingItem]);

  const ghostModel2D = useMemo(() => {
    if (!pendingItem || pendingItem.type !== EPendingType.MODEL_2D) return null;
    const data = pendingItem.data as IPendingModel2DData;
    // Return a dummy object matching the props expected by <Model2D />
    return {
      ...data.model2D,
      uuid: 'ghost-model',
      position: new Vector3(0, 0, 0),
      // FIX: Use the actual rotation from the fetched model
      rotation: data.model2D.rotation ?? 0,
      filePath: data.model2D.filePath, // Ensure path is mapped
      dimensions: {
        length: data.model2D.dimensions.length ?? 0,
        depth: data.model2D.dimensions.depth ?? 0,
        height: 0,
      },
    };
  }, [pendingItem]);

  const {
    selectedElement,
    selectedStandardWall,
    selectedPartitionWall,
    selectedProduct,
    selectedModel2D,
    selectedWindow,
    selectedDoor,
    selectedPassage,
    selectedRadiator,
    handleElementClick,
    handleWallClick,
    handlePartitionWallClick,
    handleProductClick,
    handleModel2DClick,
    handleWindowClick,
    handleDoorClick,
    handlePassageClick,
    handleRadiatorClick,
  } = useSelection();

  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const [tempPartitionWall, setTempPartitionWall] = useState<IWall>();
  const [tempPartitionWallStartPoint, setTempPartitionWallStartPoint] = useState<Vector3>();
  const partitionWallCreationActive = useRef(false);
  const lastWorldPointRef = useRef<Vector3 | null>(null);
  const partitionSourceSpaceUuidRef = useRef<string | undefined>(undefined);
  const partitionDragSourceSpaceUuidRef = useRef<string | undefined>(undefined);

  const dragRaycaster = useMemo(() => new THREE.Raycaster(), []);
  const floorMeshesRef = useRef<THREE.Mesh[]>([]);
  const floorTargetsRef = useRef<FloorTarget[]>([]);
  const highlightedFloorRef = useRef<HighlightEntry[]>([]);
  const TEMP_DOWN = useMemo(() => new THREE.Vector3(0, 0, -1), []);
  const footprintBox = useMemo(() => new THREE.Box2(), []);
  const footprintPoint = useMemo(() => new THREE.Vector2(), []);
  const footprintSamplePoint = useMemo(() => new THREE.Vector3(), []);

  const getDominantSpaceUuidForSegment = useCallback(
    (start: THREE.Vector3, end: THREE.Vector3, samples = 9): string | undefined => {
      if (!start || !end) return undefined;
      if (floorMeshesRef.current.length === 0) return undefined;
      const counts: Record<string, { count: number }> = {};
      const samplePoint = new THREE.Vector3();
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        samplePoint.lerpVectors(start, end, t);
        samplePoint.z = 100;
        dragRaycaster.set(samplePoint, TEMP_DOWN);
        const intersects = dragRaycaster.intersectObjects(floorMeshesRef.current, false);
        if (!intersects.length) continue;
        const mesh = intersects[0].object as THREE.Mesh;
        const uuid: string | undefined = mesh.userData.spaceUuid;
        if (!uuid) continue;
        if (!counts[uuid]) counts[uuid] = { count: 0 };
        counts[uuid].count++;
      }
      let bestUuid: string | undefined;
      let bestCount = 0;
      Object.entries(counts).forEach(([uuid, { count }]) => {
        if (count > bestCount) {
          bestCount = count;
          bestUuid = uuid;
        }
      });
      return bestUuid;
    },
    [dragRaycaster, TEMP_DOWN],
  );

  useEffect(() => {
    const meshes: THREE.Mesh[] = [];
    const targets: FloorTarget[] = [];
    scene.traverse((child) => {
      if (child.userData?.isFloor && child instanceof THREE.Mesh) {
        meshes.push(child);

        const box3 = new THREE.Box3().setFromObject(child);
        targets.push({
          mesh: child,
          box: new THREE.Box2(new THREE.Vector2(box3.min.x, box3.min.y), new THREE.Vector2(box3.max.x, box3.max.y)),
        });
      }
    });
    floorMeshesRef.current = meshes;
    floorTargetsRef.current = targets;
  }, [scene, building.rooms, standardWalls, partitionWalls]);

  const getItemFootprint = useCallback((itemType: ItemType, item: any) => {
    let width = 0;
    let length = 0;
    let rotation = 0;
    if (itemType === 'element') {
      width = item.width ?? 0;
      length = item.length ?? 0;
      rotation = item.rotation ?? 0;
    } else if (itemType === 'product') {
      const fp = getFootprintFromRegistry(item);
      if (fp) {
        width = fp.width;
        length = fp.length;
      } else {
        const widthMm = item.dimensions?.depth ?? item.depth ?? 0;
        const lengthMm = item.dimensions?.length ?? item.length ?? 0;
        width = widthMm * MM_TO_INTERNAL_FACTOR;
        length = lengthMm * MM_TO_INTERNAL_FACTOR;
      }
      rotation = item.rotation ?? 0;
    } else if (itemType === 'model2D') {
      const fp = getFootprintFromRegistry(item);
      if (fp) {
        ({ width, length } = fp);
      } else {
        const widthMm = item.dimensions?.depth ?? item.depth ?? 0;
        const lengthMm = item.dimensions?.length ?? item.length ?? 0;
        width = widthMm * MM_TO_INTERNAL_FACTOR;
        length = lengthMm * MM_TO_INTERNAL_FACTOR;
      }
      rotation = item.rotation ?? 0;
    }
    return { width, length, rotation };
  }, []);

  const getCandidateFloorTargets = useCallback(
    (width: number, length: number, rotation: number, position: THREE.Vector3) => {
      const targets = floorTargetsRef.current;
      if (targets.length === 0) return [];

      footprintBox.makeEmpty();

      const halfW = Math.max(0, width / 2);
      const halfL = Math.max(0, length / 2);

      if (halfW <= EPS && halfL <= EPS) {
        footprintPoint.set(position.x, position.y);
        footprintBox.expandByPoint(footprintPoint);
      } else {
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);

        footprintPoint.set(position.x + halfW * cos - halfL * sin, position.y + halfW * sin + halfL * cos);
        footprintBox.expandByPoint(footprintPoint);
        footprintPoint.set(position.x + halfW * cos + halfL * sin, position.y + halfW * sin - halfL * cos);
        footprintBox.expandByPoint(footprintPoint);
        footprintPoint.set(position.x - halfW * cos - halfL * sin, position.y - halfW * sin + halfL * cos);
        footprintBox.expandByPoint(footprintPoint);
        footprintPoint.set(position.x - halfW * cos + halfL * sin, position.y - halfW * sin - halfL * cos);
        footprintBox.expandByPoint(footprintPoint);
      }

      return targets.reduce<FloorCandidate[]>((candidates, target) => {
        if (!boxesOverlap2D(footprintBox, target.box)) return candidates;

        candidates.push({
          ...target,
          overlapArea: boxOverlapArea2D(footprintBox, target.box),
        });

        return candidates;
      }, []);
    },
    [footprintBox, footprintPoint],
  );

  const getBestFloorHit = useCallback(
    (width: number, length: number, rotation: number, position: THREE.Vector3) => {
      const candidateFloorTargets = getCandidateFloorTargets(width, length, rotation, position);
      if (candidateFloorTargets.length === 0) return null;

      const halfW = width / 2;
      const halfL = length / 2;
      const candidateFloorMeshes = candidateFloorTargets.map(({ mesh }) => mesh);
      const hitCounts: Record<string, { count: number; mesh: THREE.Mesh }> = {};

      const countHitAt = (pt: THREE.Vector3) => {
        dragRaycaster.set(pt, TEMP_DOWN);
        const intersects = dragRaycaster.intersectObjects(candidateFloorMeshes, false);
        if (intersects.length > 0) {
          const mesh = intersects[0].object as THREE.Mesh;
          const uuid = mesh.userData.spaceUuid;
          if (uuid) {
            if (!hitCounts[uuid]) hitCounts[uuid] = { count: 0, mesh };
            hitCounts[uuid].count++;
          }
        }
      };

      footprintSamplePoint.copy(position);
      footprintSamplePoint.z = 100;
      countHitAt(footprintSamplePoint);

      TEMP_ROT.set(0, 0, rotation);
      FOOTPRINT_SAMPLE_FACTORS.forEach(([widthFactor, lengthFactor]) => {
        footprintSamplePoint.set(halfW * widthFactor, halfL * lengthFactor, 0);
        footprintSamplePoint.applyEuler(TEMP_ROT);
        footprintSamplePoint.add(position);
        footprintSamplePoint.z = 100;
        countHitAt(footprintSamplePoint);
      });

      let maxHits = 0;
      let winner: { count: number; mesh: THREE.Mesh } | null = null;
      Object.values(hitCounts).forEach((hit) => {
        if (hit.count > maxHits) {
          maxHits = hit.count;
          winner = hit;
        }
      });
      if (winner) return winner.mesh;

      const fallbackTarget = candidateFloorTargets.reduce<FloorCandidate | null>((best, candidate) => {
        if (!best) return candidate;
        return candidate.overlapArea > best.overlapArea ? candidate : best;
      }, null);

      return fallbackTarget?.mesh ?? null;
    },
    [dragRaycaster, getCandidateFloorTargets, footprintSamplePoint, TEMP_DOWN],
  );

  type DesiredHighlight = { mesh: THREE.Mesh; colorHex: number };

  const updateItemHighlight = useCallback(
    (width: number, length: number, rotation: number, position: THREE.Vector3, sourceSpaceUuid?: string, forcedTargetSpaceUuid?: string | null) => {
      if (!position) return;
      const prev = Array.isArray(highlightedFloorRef.current) ? highlightedFloorRef.current : [];
      const desired: DesiredHighlight[] = [];
      let sourceMesh: THREE.Mesh | null = null;
      if (sourceSpaceUuid) {
        sourceMesh = floorMeshesRef.current.find((m) => m.userData.spaceUuid === sourceSpaceUuid) ?? null;
      }

      if (forcedTargetSpaceUuid !== undefined) {
        let targetMesh: THREE.Mesh | null = null;
        if (forcedTargetSpaceUuid) {
          targetMesh = floorMeshesRef.current.find((m) => m.userData.spaceUuid === forcedTargetSpaceUuid) ?? null;
        }
        const hasSource = !!sourceSpaceUuid;
        if (!targetMesh) {
          if (hasSource && sourceMesh) desired.push({ mesh: sourceMesh, colorHex: FLOOR_HIGHLIGHT_SOURCE_COLOR });
        } else {
          if (!hasSource) {
            desired.push({ mesh: targetMesh, colorHex: FLOOR_HIGHLIGHT_TARGET_COLOR });
          } else if (forcedTargetSpaceUuid === sourceSpaceUuid) {
            // no op
          } else {
            if (sourceMesh) desired.push({ mesh: sourceMesh, colorHex: FLOOR_HIGHLIGHT_SOURCE_COLOR });
            desired.push({ mesh: targetMesh, colorHex: FLOOR_HIGHLIGHT_TARGET_COLOR });
          }
        }
      } else {
        const hitMesh = getBestFloorHit(width, length, rotation, position);
        if (!hitMesh) {
          if (sourceMesh && sourceSpaceUuid) {
            desired.push({ mesh: sourceMesh, colorHex: FLOOR_HIGHLIGHT_SOURCE_COLOR });
          }
        } else {
          const hitUuid: string | undefined = hitMesh.userData.spaceUuid;
          if (!sourceSpaceUuid) {
            desired.push({ mesh: hitMesh, colorHex: FLOOR_HIGHLIGHT_TARGET_COLOR });
          } else if (hitUuid && hitUuid !== sourceSpaceUuid) {
            if (sourceMesh) desired.push({ mesh: sourceMesh, colorHex: FLOOR_HIGHLIGHT_SOURCE_COLOR });
            desired.push({ mesh: hitMesh, colorHex: FLOOR_HIGHLIGHT_TARGET_COLOR });
          }
        }
      }
      if (!desired.length) {
        prev.forEach(restoreFloorHighlight);
        highlightedFloorRef.current = [];
        return;
      }

      if (areHighlightTargetsEqual(prev, desired)) return;

      prev.forEach(restoreFloorHighlight);
      const nextEntries: HighlightEntry[] = [];
      desired.forEach(({ mesh, colorHex }) => {
        const entry = applyFloorHighlight(mesh, colorHex);
        if (entry) nextEntries.push(entry);
      });
      highlightedFloorRef.current = nextEntries;
    },
    [getBestFloorHit],
  );

  const findSourceSpaceByItemRef = useCallback(
    (arrayKey: 'elements' | 'products' | 'models2D', itemRef: any): string | undefined => {
      if (!itemRef) return undefined;
      // Removed check for itemRef.spaceId as it doesn't exist on elements
      if (!building.rooms) return undefined;
      const itemKey = itemRef.uuid ?? itemRef.id;
      if (!itemKey) return undefined;

      for (const room of building.rooms) {
        if (!room.spaces) continue;
        for (const space of room.spaces as any[]) {
          const arr = space[arrayKey] as any[] | undefined;
          if (!Array.isArray(arr)) continue;
          if (arr.some((x) => (x?.uuid ?? x?.id) === itemKey)) {
            return space.uuid;
          }
        }
      }
      return undefined;
    },
    [building.rooms],
  );

  const clearHighlight = useCallback(() => {
    const entries = Array.isArray(highlightedFloorRef.current) ? highlightedFloorRef.current : [];
    entries.forEach(restoreFloorHighlight);
    highlightedFloorRef.current = [];
  }, []);

  const getSpaceUuidAtWorldPoint = useCallback(
    (position: THREE.Vector3 | null | undefined): string | undefined => {
      if (!position) return undefined;
      const mesh = getBestFloorHit(0, 0, 0, position);
      return mesh?.userData?.spaceUuid;
    },
    [getBestFloorHit],
  );

  const handleItemDragEnd = useCallback(
    (itemType: ItemType, index: number, item: any, finalPosition: THREE.Vector3) => {
      const { width, length, rotation } = getItemFootprint(itemType, item);
      const hitMesh = getBestFloorHit(width, length, rotation, finalPosition);
      const targetSpaceUuid: string | undefined = hitMesh ? hitMesh.userData.spaceUuid : undefined;

      clearHighlight();

      setBuilding((prev) => {
        // Deep clone entire object to ensure state updates trigger re-renders
        const next = { ...prev };
        const itemKey = item.uuid ?? item.id;

        if (!itemKey) return prev;

        const arrayKey: 'elements' | 'products' | 'models2D' = itemType === 'element' ? 'elements' : itemType === 'product' ? 'products' : 'models2D';

        let movingItem: any = null;

        // 1. Remove from Root (if exists)
        if (next[arrayKey]) {
          const idx = next[arrayKey]!.findIndex((x: any) => (x.uuid ?? x.id) === itemKey);
          if (idx !== -1) {
            const newArr = [...next[arrayKey]!];
            [movingItem] = newArr.splice(idx, 1);
            next[arrayKey] = newArr;
          }
        }

        // 2. Remove from Space (if exists)
        if (next.rooms) {
          next.rooms = next.rooms.map((room) => {
            if (!room.spaces) return room;

            let roomChanged = false;
            const newSpaces = room.spaces.map((space) => {
              const arr = space[arrayKey];
              if (Array.isArray(arr)) {
                const idx = arr.findIndex((x: any) => (x.uuid ?? x.id) === itemKey);
                if (idx !== -1) {
                  const newArr = [...arr];
                  const [removed] = newArr.splice(idx, 1);
                  if (!movingItem) movingItem = removed;

                  roomChanged = true;
                  const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                  newSpace[arrayKey] = newArr;
                  return newSpace;
                }
              }
              return space;
            });

            if (roomChanged) {
              const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
              newRoom.spaces = newSpaces;
              return newRoom;
            }
            return room;
          });
        }

        // 3. Fallback
        if (!movingItem) {
          movingItem = Object.assign(Object.create(Object.getPrototypeOf(item)), item);
        }

        // 4. Update Position
        if (movingItem.position instanceof THREE.Vector3 && finalPosition) {
          movingItem.position.copy(finalPosition);
        } else if (finalPosition) {
          movingItem.position = finalPosition.clone();
        }

        // 5. Add to Target Space or Root
        if (targetSpaceUuid) {
          let added = false;
          next.rooms = next.rooms?.map((room) => {
            if (!room.spaces) return room;
            const newSpaces = room.spaces.map((space) => {
              if (space.uuid === targetSpaceUuid) {
                added = true;
                const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                const currentArr = newSpace[arrayKey] ? [...newSpace[arrayKey]] : [];
                currentArr.push(movingItem);
                newSpace[arrayKey] = currentArr;
                return newSpace;
              }
              return space;
            });

            if (added) {
              const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
              newRoom.spaces = newSpaces;
              return newRoom;
            }
            return room;
          });
        } else {
          // Add to Root
          const newRootArr = next[arrayKey] ? [...next[arrayKey]!] : [];
          newRootArr.push(movingItem);
          next[arrayKey] = newRootArr;
        }

        return next;
      });
    },
    [getBestFloorHit, setBuilding, clearHighlight, getItemFootprint],
  );

  // --- WALL LOGIC ---
  const resetDrawingState = useCallback(() => {
    setTempPartitionWall(undefined);
    setTempPartitionWallStartPoint(undefined);
    partitionSourceSpaceUuidRef.current = undefined;
    clearHighlight();
    setWallMode(EWallMode.NONE);
    setTimeout(() => {
      partitionWallCreationActive.current = false;
      globalClickLock = false;
    }, 100);
  }, [setWallMode, clearHighlight]);

  useEffect(() => {
    if (wallMode !== EWallMode.PARTITION) {
      setTempPartitionWall(undefined);
      setTempPartitionWallStartPoint(undefined);
      partitionSourceSpaceUuidRef.current = undefined;
      partitionWallCreationActive.current = false;
      globalClickLock = false;
      clearHighlight();
    }
  }, [wallMode, clearHighlight]);
  const toAttr = (a: any) =>
    a instanceof AttributeModel
      ? a
      : new AttributeModel(
          a.id,
          a.key,
          a.value,
          a.predecessorId,
          a.type,
          a.unit,
          a.hidden,
          a.forced,
          a.ownerId,
          a.userId,
          a.publicFlag,
          a.localization,
          a.defaults,
          a.validation,
          a.unitFamily,
        );

  const { cameraPositionSprites, updateCameraPositionSprites } = useAnnotations();

  const cloneWithProto = <T,>(obj: T): T => Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);

  const createPartitionWall = useCallback(
    (startPoint: Vector3, endPoint: Vector3, targetSpaceUuid?: string | null): PartitionWallModel => {
      const s = new Vector3(startPoint.x, startPoint.y, startPoint.z);
      const e = new Vector3(endPoint.x, endPoint.y, endPoint.z);

      const wall = new PartitionWallModel(s, e);

      // 1) Add to global walls IMMEDIATELY
      setBuildingWalls((currentWalls: IWall[]) => [...currentWalls, wall], EHistoryAction.WALL_PARTITION_ADDED);

      // 2) Optionally add to a specific space.walls IMMEDIATELY
      if (targetSpaceUuid) {
        setBuilding((prev) => {
          const next: any = { ...prev };

          (next.rooms ?? []).forEach((room: any) => {
            (room.spaces ?? []).forEach((space: any) => {
              if (space.uuid !== targetSpaceUuid) return;

              const existingWalls: IWall[] = Array.isArray(space.walls) ? [...space.walls] : [];
              if (!existingWalls.some((w) => w && w.uuid === wall.uuid)) {
                existingWalls.push(wall);
                space.setWalls(existingWalls);
              }
            });
          });

          return next;
        });
      }

      // 3) Fetch attributes asynchronously in the background
      fetchAttributesForObject(wall, ObjectType.Wall, regionCode)
        .then((attrs) => {
          wall.attributes = updateWallGeometryAttributes(wall, unitType, (attrs ?? []).map(toAttr));
          // Force state update to reflect new attributes
          setBuildingWalls((currentWalls: IWall[]) =>
            currentWalls.map((w) => {
              if (w.uuid === wall.uuid) {
                // FIX: Verhindert das Überschreiben der StandardWall mit der veralteten PartitionWall!
                if (w.type !== EWall.PARTITION) {
                  const upgradedWall = Object.assign(Object.create(Object.getPrototypeOf(w)), w);
                  upgradedWall.attributes = wall.attributes;
                  return upgradedWall;
                }
                return wall;
              }
              return w;
            }),
          );
        })
        .catch((err) => {
          console.warn('[createPartitionWall] fetchAttributes failed, leaving attrs empty', err);
          wall.attributes = [];
        });
      return wall;
    },
    [unitType, setBuildingWalls, setBuilding, regionCode],
  );

  const handleFinishPartitionWall = useCallback(
    (endPoint: Vector3) => {
      if (!tempPartitionWallStartPoint) return;
      if (partitionWallCreationActive.current || globalClickLock) return;
      partitionWallCreationActive.current = true;
      globalClickLock = true;

      const SNAP_TOLERANCE = 0.15; // Toleranz für das Andocken
      const rawStart = tempPartitionWallStartPoint.clone();
      const rawEnd = endPoint.clone();
      const placementStart = rawStart.clone();
      const placementEnd = rawEnd.clone();

      const sourceSpaceUuid = partitionSourceSpaceUuidRef.current;
      const dominantSpaceUuid = getDominantSpaceUuidForSegment(rawStart, rawEnd);
      const primarySpaceUuid = dominantSpaceUuid ?? sourceSpaceUuid;
      let splitTarget = findPartitionSplitTargetInBuilding(building, rawStart, rawEnd, primarySpaceUuid, SNAP_TOLERANCE);

      if (!splitTarget && sourceSpaceUuid && sourceSpaceUuid !== primarySpaceUuid) {
        splitTarget = findPartitionSplitTargetInBuilding(building, rawStart, rawEnd, sourceSpaceUuid, SNAP_TOLERANCE);
      }

      if (!splitTarget && !primarySpaceUuid) {
        splitTarget = findPartitionSplitTargetInBuilding(building, rawStart, rawEnd, undefined, SNAP_TOLERANCE);
      }

      standardWalls.forEach((w) => {
        if (!w || !w.startPoint || !w.endPoint) return;

        const startInfo = pointToSegmentInfo(w.startPoint, w.endPoint, placementStart);
        const endInfo = pointToSegmentInfo(w.startPoint, w.endPoint, placementEnd);

        if (startInfo.dist <= SNAP_TOLERANCE) {
          placementStart.copy(startInfo.Q);
          placementStart.z = 0;
        }

        if (endInfo.dist <= SNAP_TOLERANCE) {
          placementEnd.copy(endInfo.Q);
          placementEnd.z = 0;
        }
      });

      const splitStart = splitTarget ? new THREE.Vector3(splitTarget.hitA.P.x, splitTarget.hitA.P.y, 0) : placementStart;
      const splitEnd = splitTarget ? new THREE.Vector3(splitTarget.hitB.P.x, splitTarget.hitB.P.y, 0) : placementEnd;
      const preferredSpaceUuid = splitTarget?.spaceUuid ?? primarySpaceUuid ?? sourceSpaceUuid;
      const dominantTargetUuid = splitTarget?.spaceUuid ?? preferredSpaceUuid ?? getDominantSpaceUuidForSegment(placementStart, placementEnd);

      if (splitTarget) {
        showConfirmationDialog(
          intl.formatMessage({ id: 'PartitionWall.ConfirmationDialog.title', defaultMessage: 'Split room?' }),
          intl.formatMessage({
            id: 'PartitionWall.ConfirmationDialog.message',
            defaultMessage: 'This line touches two different walls. Split the room and convert it to a standard wall?',
          }),
          () => {
            closeConfirmationDialog();

            const buildingBefore = building;
            const sourceSpaceUuid = splitTarget.spaceUuid ?? preferredSpaceUuid ?? getDominantSpaceUuidForSegment(splitStart, splitEnd);
            const spaceToRoom = buildSpaceToRoomIndex(buildingBefore);
            const sourceRoomUuid = sourceSpaceUuid ? spaceToRoom.get(sourceSpaceUuid) : undefined;
            const keysToDelete = sourceRoomUuid ? collectSpriteKeysInRoom(buildingBefore, cameraPositionSprites, sourceRoomUuid, getSpaceUuidAtPointByGeometry) : new Set<string>();

            // FIX: Übergib zwingend die sourceSpaceUuid, damit die Wand vor dem Split dem Raum zugeordnet ist!
            const wall = createPartitionWall(splitStart, splitEnd, sourceSpaceUuid);

            if (keysToDelete.size) {
              updateCameraPositionSprites((prev) => (prev ?? []).filter((spr: any) => !keysToDelete.has(spriteKey(spr))));
            }

            finalizePartitionWallIfSplitsRoomByUuid(wall.uuid);
            resetDrawingState();
          },
          () => {
            closeConfirmationDialog();
            createPartitionWall(placementStart, placementEnd, dominantTargetUuid);
            setTimeout(() => {
              resetDrawingState();
            }, 50);
          },
        );
      } else {
        createPartitionWall(placementStart, placementEnd, dominantTargetUuid);
        resetDrawingState();
      }
    },
    [
      tempPartitionWallStartPoint,
      standardWalls,
      showConfirmationDialog,
      intl,
      createPartitionWall,
      finalizePartitionWallIfSplitsRoomByUuid,
      closeConfirmationDialog,
      resetDrawingState,
      getDominantSpaceUuidForSegment,
      building,
      cameraPositionSprites,
      updateCameraPositionSprites,
    ],
  );
  const ensureVec3 = (p: any): THREE.Vector3 => {
    if (!p) return new THREE.Vector3(0, 0, 0);
    if (p instanceof THREE.Vector3) return p;
    return new THREE.Vector3(p.x ?? 0, p.y ?? 0, p.z ?? 0);
  };
  useEffect(() => {
    updateCameraPositionSprites((prev) => {
      let changed = false;
      const next: any[] = [];

      for (const spr of prev ?? []) {
        if (!spr?.position) continue;

        const newUuid = getSpaceUuidAtPointByGeometry(building, spr.position) ?? null;

        if (!newUuid) {
          changed = true;
          continue;
        }

        if (spr.spaceUuid !== newUuid) {
          const clone = Object.assign(Object.create(Object.getPrototypeOf(spr)), spr);
          clone.spaceUuid = newUuid;
          next.push(clone);
          changed = true;
        } else {
          next.push(spr);
        }
      }

      return changed ? next : prev;
    });
  }, [building.rooms, updateCameraPositionSprites]);
  const attachPartitionWallToSpace = useCallback(
    (wallIndex: number, targetSpaceUuid?: string) => {
      setBuilding((prev) => {
        const next = prev as any;
        const walls = next.walls as IWall[] | undefined;
        if (!Array.isArray(walls)) return prev;
        const wall = walls[wallIndex] as IWall | undefined;
        if (!wall) return prev;
        const wallUuid = wall.uuid;
        let touched = false;

        (next.rooms ?? []).forEach((room: any) => {
          (room.spaces ?? []).forEach((space: any) => {
            if (!Array.isArray(space.walls)) return;
            const idx = space.walls.findIndex((w: IWall) => w && w.uuid === wallUuid);
            if (idx !== -1) {
              const newWalls = space.walls.slice();
              newWalls.splice(idx, 1);
              space.setWalls(newWalls);
              touched = true;
            }
          });
        });

        if (targetSpaceUuid) {
          const targetRoom = (next.rooms ?? []).find((r: any) => (r.spaces ?? []).some((s: any) => s.uuid === targetSpaceUuid));
          const targetSpace = targetRoom?.spaces?.find((s: any) => s.uuid === targetSpaceUuid) as any | undefined;
          if (targetSpace) {
            const existing = Array.isArray(targetSpace.walls) ? targetSpace.walls : [];
            if (!existing.some((w: IWall) => w && w.uuid === wallUuid)) {
              targetSpace.setWalls([...existing, wall]);
              touched = true;
            }
          }
        }
        return touched ? { ...next } : prev;
      });
    },
    [setBuilding],
  );

  const handleWallUpdate = useCallback(
    (wallUuid: string, updatedWallModel: IWall, action?: EHistoryAction) => {
      const nextUuid = updatedWallModel?.uuid;
      // Optional debug:

      // 1) Update the flat walls list (by uuid, not index)
      setBuildingWalls(
        (prevWalls) => prevWalls.map((w) => (w && (w.uuid === wallUuid || (nextUuid && w.uuid === nextUuid)) ? updatedWallModel : w)),
        action ?? EHistoryAction.WALLS_CHANGED,
      );

      // 2) Update rooms / spaces / boundaries that refer to this wall
      setBuilding((prev) => {
        const matchUuids = new Set<string>();
        if (wallUuid) matchUuids.add(wallUuid);
        if (nextUuid) matchUuids.add(nextUuid);

        let touched = false;

        (prev.rooms ?? []).forEach((room) => {
          (room.spaces ?? []).forEach((space: any) => {
            // ---- space.walls ----
            if (Array.isArray(space.walls) && space.walls.length) {
              const contains = space.walls.some((w: IWall) => w && matchUuids.has(w.uuid));

              if (contains) {
                const newWalls = space.walls.map((w: IWall) => (w && matchUuids.has(w.uuid) ? updatedWallModel : w));
                space.setWalls(newWalls.slice());
                touched = true;
              }
            }

            // ---- boundaries ----
            (space.boundaries ?? []).forEach((b: any) => {
              if (!(b instanceof PhysicalBoundaryModel)) return;

              // boundary.walls
              if (Array.isArray(b.walls) && b.walls.length) {
                const newBWalls = b.walls.map((w: IWall) => (w && matchUuids.has(w.uuid) ? updatedWallModel : w));
                for (let i = 0; i < newBWalls.length; i++) {
                  if (newBWalls[i] !== b.walls[i]) {
                    b.setWalls(newBWalls);
                    touched = true;
                    break;
                  }
                }
              }

              // boundary.edges[].wall
              const edges = (b as any).edges as Array<{ wall: IWall }>;
              if (Array.isArray(edges) && edges.length) {
                let changed = false;
                edges.forEach((e) => {
                  const uw = e?.wall;
                  if (uw && matchUuids.has(uw.uuid) && uw !== updatedWallModel) {
                    e.wall = updatedWallModel;
                    changed = true;
                  }
                });
                if (changed) {
                  // keep any cached points in sync if your model supports it
                  (b as any).resetPoints?.();
                  touched = true;
                }
              }
            });
          });
        });

        return touched ? { ...prev } : prev;
      });
    },
    [setBuildingWalls, setBuilding],
  );

  const handlePartitionWallUpdate = useCallback(
    (wallUuid: string, updatedWallModel: IWall, action?: EHistoryAction) => {
      // Find its index in the current walls array
      const index = building.walls.findIndex((w: IWall) => w && w.uuid === wallUuid);

      const prevUuid = building.walls[index]?.uuid;
      const nextUuid = updatedWallModel?.uuid;

      // 1) Old index-based update for the flat walls array
      setBuildingWalls((prevWalls) => prevWalls.map((w, i) => (i === index ? updatedWallModel : w)), action ?? EHistoryAction.WALLS_CHANGED);

      // 2) Same propagation into rooms/spaces/boundaries
      setBuilding((prev) => {
        const matchUuids = new Set<string>();
        if (prevUuid) matchUuids.add(prevUuid);
        if (nextUuid) matchUuids.add(nextUuid);

        let touched = false;

        (prev.rooms ?? []).forEach((room) => {
          (room.spaces ?? []).forEach((space: any) => {
            // space.walls
            if (Array.isArray(space.walls) && space.walls.length) {
              const contains = space.walls.some((w: IWall) => w && matchUuids.has(w.uuid));
              if (contains) {
                const newWalls = space.walls.map((w: IWall) => (matchUuids.has(w.uuid) ? updatedWallModel : w));
                space.setWalls(newWalls.slice());
                touched = true;
              }
            }

            // boundaries – usually no partition walls here, but keeping behaviour consistent:
            (space.boundaries ?? []).forEach((b: any) => {
              if (!(b instanceof PhysicalBoundaryModel)) return;

              if (Array.isArray(b.walls) && b.walls.length) {
                const newBWalls = b.walls.map((w: IWall) => (w && matchUuids.has(w.uuid) ? updatedWallModel : w));
                for (let i = 0; i < newBWalls.length; i++) {
                  if (newBWalls[i] !== b.walls[i]) {
                    b.setWalls(newBWalls);
                    touched = true;
                    break;
                  }
                }
              }

              const edges = (b as any).edges as Array<{ wall: IWall }>;
              if (Array.isArray(edges) && edges.length) {
                let changed = false;
                edges.forEach((e) => {
                  const uw = e?.wall;
                  if (uw && matchUuids.has(uw.uuid) && uw !== updatedWallModel) {
                    e.wall = updatedWallModel;
                    changed = true;
                  }
                });
                if (changed) touched = true;
              }
            });
          });
        });

        return touched ? { ...prev } : prev;
      });
    },
    [building.walls, setBuildingWalls, setBuilding, handleWallUpdate],
  );

  const handlePointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (e.button !== 0) return;
      if (!e.point) return;

      // --- NEW: Pending Item Placement Click ---
      if (pendingItem && e.point) {
        e.stopPropagation();

        // 1. Calculate space at drop location
        let length = 0,
          rotation = 0,
          width = 0;

        if (pendingItem.type === EPendingType.ELEMENT) {
          const d = pendingItem.data as any;
          width = d.width;
          length = d.length;
          rotation = d.rotation;
        } else if (pendingItem.type === EPendingType.PRODUCT) {
          const p = (pendingItem.data as IPendingProductData).productModel;
          width = (p.dimensions?.depth ?? p.depth ?? 0) * MM_TO_INTERNAL_FACTOR;
          length = (p.dimensions?.length ?? p.length ?? 0) * MM_TO_INTERNAL_FACTOR;
          rotation = p.rotation ?? 0;
        } else if (pendingItem.type === EPendingType.MODEL_2D) {
          const m = (pendingItem.data as IPendingModel2DData).model2D;
          width = (m.dimensions?.depth ?? m.depth ?? 0) * MM_TO_INTERNAL_FACTOR;
          length = (m.dimensions?.length ?? m.length ?? 0) * MM_TO_INTERNAL_FACTOR;
          rotation = m.rotation ?? 0;
        }

        const hitMesh = getBestFloorHit(width, length, rotation, e.point);
        const spaceUuid = hitMesh?.userData?.spaceUuid;

        // 2. Confirm placement with CLONED position
        pendingItem.onConfirm(e.point.clone(), spaceUuid);

        cancelPlacement();
        clearHighlight();
        setGhostPosition(null);
        return;
      }
      // -----------------------------------------

      if (wallMode === EWallMode.PARTITION) {
        e.stopPropagation();
        if (partitionWallCreationActive.current || globalClickLock) return;
        const p = e.point;
        if (!p) return;
        if (!tempPartitionWallStartPoint) {
          globalClickLock = true;
          setTimeout(() => (globalClickLock = false), 100);
          setTempPartitionWallStartPoint(p);
          setTempPartitionWall(new PartitionWallModel(p, p));
          partitionSourceSpaceUuidRef.current = getSpaceUuidAtWorldPoint(p.clone());
        } else {
          handleFinishPartitionWall(p);
        }
        return;
      }
      if (drawEnabled) {
        // In draw mode: ONLY drawing is allowed
        if (isDrawingAllowed) createWall(e.point.x, e.point.y, e);
        return;
      }
    },
    [
      isDrawingAllowed,
      createWall,
      wallMode,
      tempPartitionWallStartPoint,
      handleFinishPartitionWall,
      getSpaceUuidAtWorldPoint,
      pendingItem,
      cancelPlacement,
      clearHighlight,
      getBestFloorHit,
    ],
  );

  const handlePartitionWallDragPreview = useCallback(
    (phase: 'start' | 'move' | 'end', start: THREE.Vector3, end: THREE.Vector3, wallIndex: number) => {
      if (phase === 'start') {
        partitionDragSourceSpaceUuidRef.current = getDominantSpaceUuidForSegment(start, end);
        return;
      }
      if (phase === 'end') {
        const dominantTargetUuid = getDominantSpaceUuidForSegment(start, end);
        partitionDragSourceSpaceUuidRef.current = undefined;
        clearHighlight();
        attachPartitionWallToSpace(wallIndex, dominantTargetUuid ?? undefined);
        return;
      }
      const sourceUuid = partitionDragSourceSpaceUuidRef.current;
      const dominantTargetUuid = getDominantSpaceUuidForSegment(start, end);
      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      updateItemHighlight(0, 0, 0, mid, sourceUuid, dominantTargetUuid ?? null);
    },
    [getDominantSpaceUuidForSegment, updateItemHighlight, clearHighlight, attachPartitionWallToSpace],
  );

  const handlePointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      const p = e.point;
      if (!p) return;
      if (drawEnabled) {
        if (isDrawing) updateWall(p.x, p.y);
        return;
      }

      // --- NEW: Pending Item Hover Logic ---
      if (pendingItem) {
        // Fix Z-Fighting: Lift lightly to Z=2
        setGhostPosition(new Vector3(p.x, p.y, 2));

        let length = 0,
          rotation = 0,
          width = 0;
        if (pendingItem.type === EPendingType.ELEMENT) {
          const d = pendingItem.data as any;
          width = d.width;
          length = d.length;
          rotation = d.rotation;
        } else if (pendingItem.type === EPendingType.PRODUCT) {
          const pItem = (pendingItem.data as IPendingProductData).productModel;
          width = (pItem.dimensions?.depth ?? 0) * MM_TO_INTERNAL_FACTOR;
          length = (pItem.dimensions?.length ?? 0) * MM_TO_INTERNAL_FACTOR;
          rotation = pItem.rotation ?? 0;
        } else if (pendingItem.type === EPendingType.MODEL_2D) {
          const mItem = (pendingItem.data as IPendingModel2DData).model2D;
          width = (mItem.dimensions?.depth ?? 0) * MM_TO_INTERNAL_FACTOR;
          length = (mItem.dimensions?.length ?? 0) * MM_TO_INTERNAL_FACTOR;
          rotation = mItem.rotation ?? 0;
        }

        updateItemHighlight(width, length, rotation, new Vector3(p.x, p.y, 0), undefined);
      }
      // -------------------------------------

      if (wallMode === EWallMode.PARTITION) {
        lastWorldPointRef.current = p;

        if (tempPartitionWallStartPoint) {
          const start = tempPartitionWallStartPoint.clone();
          const end = p.clone();
          end.z = 0;

          setTempPartitionWall(new PartitionWallModel(start, end));

          const dominantSpaceUuid = getDominantSpaceUuidForSegment(start, end);
          updateItemHighlight(0, 0, 0, end, undefined, dominantSpaceUuid ?? null);
        }
      }
    },
    [isDrawing, updateWall, wallMode, tempPartitionWallStartPoint, getDominantSpaceUuidForSegment, updateItemHighlight, pendingItem],
  );

  return (
    <group name="group-building" renderOrder={0}>
      {isDrawing && isDrawingAllowed && previewWalls.map((wall) => <PreviewWall key={`preview-${wall.uuid}`} wall={wall} isIntersecting={false} />)}

      {isDrawing && isDrawingAllowed && tempWall && <PreviewWall wall={tempWall} isIntersecting={hasIntersectingWalls} />}
      {tempPartitionWall && <PreviewWall wall={tempPartitionWall} />}

      {/* Render Walls */}
      {(building.walls ?? []).map((wall, i) => {
        if (!(wall instanceof StandardWallModel)) return null;
        return (
          <StandardWall
            key={`std-${wall.uuid}`}
            index={i}
            onWallUpdate={handleWallUpdate}
            selectedWall={selectedStandardWall}
            selectedWindow={selectedWindow}
            selectedDoor={selectedDoor}
            selectedPassage={selectedPassage}
            selectedRadiator={selectedRadiator}
            isPreview={isDrawing}
            onClick={async (e) => {
              if (drawEnabled) return;
              if (isDrawing) return;
              if (wallMode === EWallMode.PARTITION) {
                e.stopPropagation();

                if (partitionWallCreationActive.current || globalClickLock) return;

                const p = e.point;
                if (!p) return;

                const centerlinePoint = projectPointToWallCenterline(wall, p);

                if (!tempPartitionWallStartPoint) {
                  globalClickLock = true;
                  setTimeout(() => (globalClickLock = false), 100);

                  setTempPartitionWallStartPoint(centerlinePoint);
                  setTempPartitionWall(new PartitionWallModel(centerlinePoint, centerlinePoint));
                  partitionSourceSpaceUuidRef.current = getSpaceUuidAtWorldPoint(centerlinePoint.clone());
                  return;
                }

                handleFinishPartitionWall(centerlinePoint);
                return;
              }
              handleWallClick(i);
            }}
            onWindowClick={handleWindowClick}
            onDoorClick={handleDoorClick}
            onPassageClick={handlePassageClick}
            onRadiatorClick={handleRadiatorClick}
            camera={camera}
            raycaster={raycaster}
            planeRef={planeRef as RefObject<Object3D>}
          />
        );
      })}

      {partitionWalls.map((wall, i) => {
        const actualIndex = building.walls.findIndex((w: IWall) => w.uuid === wall.uuid);
        return (
          <PartitionWall
            key={`part-${wall.uuid}`}
            index={actualIndex}
            selectedWall={selectedPartitionWall}
            onClick={() => {
              if (drawEnabled) return; // NEW
              !isDrawing && handlePartitionWallClick(i);
            }}
            onDragPreview={(phase, start, end) => {
              if (drawEnabled) return; // NEW: no dragging during draw
              handlePartitionWallDragPreview(phase, start, end, actualIndex);
            }}
            onWallUpdate={handlePartitionWallUpdate}
            camera={camera}
            raycaster={raycaster}
            planeRef={planeRef as RefObject<Object3D>}
            onWindowClick={handleWindowClick}
            onDoorClick={handleDoorClick}
            onPassageClick={handlePassageClick}
            onRadiatorClick={handleRadiatorClick}
            selectedWindow={selectedWindow}
            selectedDoor={selectedDoor}
            selectedPassage={selectedPassage}
            selectedRadiator={selectedRadiator}
          />
        );
      })}

      {drawEnabled &&
        dockingPoints.map((p, i) => (
          <DockingPointMesh
            key={`dock-${i}`}
            position={p}
            onClick={(e) => {
              e.stopPropagation();
              if (isDrawingAllowed) {
                createWall(p.x, p.y, e);
              }
            }}
          />
        ))}
      {/* RENDER ELEMENTS (Flattened List) */}
      {allElements.map((element: IElement, i) => {
        const sourceSpaceUuid = findSourceSpaceByItemRef('elements', element);
        return (
          <Element
            key={element.uuid}
            element={element}
            position={new THREE.Vector3(element.position.x, element.position.y, 2)}
            selectedElement={selectedElement}
            index={i}
            onClick={() => !isDrawing && handleElementClick(i)}
            onPositionChange={(newPosition) => {
              element.position.set(newPosition[0], newPosition[1], newPosition[2]);
              updateElement(i, element);
              updateItemHighlight(element.width, element.length, element.rotation, new THREE.Vector3(...newPosition), sourceSpaceUuid);
            }}
            onDragEnd={(el, pos) => handleItemDragEnd('element', i, el, pos)}
            onRotationChange={(newRotation) => {
              element.rotation = newRotation;
              updateElement(i, element);
            }}
          />
        );
      })}

      {/* RENDER PRODUCTS (Flattened List) */}
      {allProducts.map((product, i) => {
        const sourceSpaceUuid = findSourceSpaceByItemRef('products', product);
        return (
          <Product
            key={`product-${product.uuid}`}
            product={product}
            svgPath={product.svgPath}
            position={[product.position.x, product.position.y, 2]}
            rotation={product.rotation}
            length={product.dimensions.length}
            depth={product.dimensions.depth}
            fillColor={product.fillColor}
            strokeColor={product.strokeColor}
            strokeWidth={product.strokeWidth}
            selectedProduct={selectedProduct}
            texture={product.texture}
            index={i}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDrawing) handleProductClick(i);
            }}
            onPositionChange={(newPosition) => {
              const vec = new THREE.Vector3(...newPosition);
              product.position = vec;
              updateProduct(product);
            }}
            onDragMove={(newPosition) => {
              const vec = new THREE.Vector3(...newPosition);
              const { width, length, rotation } = getItemFootprint('product', product);
              updateItemHighlight(width, length, rotation, vec, sourceSpaceUuid);
            }}
            onRotationChange={(newRotation) => {
              product.rotation = newRotation;
              updateProduct(product);
            }}
            onDragEnd={(finalPos) => {
              handleItemDragEnd('product', i, product, finalPos);
            }}
          />
        );
      })}

      {/* RENDER MODELS 2D (Flattened List) */}
      {allModels2D.map((model, i) => {
        const sourceSpaceUuid = findSourceSpaceByItemRef('models2D', model);
        const lengthMM = (model as any).dimensions?.length ?? (model as any).length ?? 0;
        const depthMM = (model as any).dimensions?.depth ?? (model as any).depth ?? 0;
        return (
          <Model2D
            key={`m2d-${model.uuid}`}
            index={i}
            model={model}
            svgPath={model.filePath}
            position={[model.position.x, model.position.y, 2]}
            length={lengthMM}
            depth={depthMM}
            texture={model.texture}
            rotation={model.rotation}
            selectedModel2D={selectedModel2D}
            onClick={(e) => {
              e.stopPropagation();
              handleModel2DClick(i);
            }}
            onPositionChange={(pos) => {
              const vec = new THREE.Vector3(...pos);
              model.position = vec;
              updateModel2D(model);
            }}
            onDragMove={(pos) => {
              const vec = new THREE.Vector3(...pos);
              const { width, length, rotation } = getItemFootprint('model2D', model);
              updateItemHighlight(width, length, rotation, vec, sourceSpaceUuid);
            }}
            onRotationChange={(rot) => {
              model.rotation = rot;
              updateModel2D(model);
            }}
            onDragEnd={(finalPos) => {
              handleItemDragEnd('model2D', i, model, finalPos);
            }}
          />
        );
      })}

      {/* RENDER GHOSTS (Position controlled by PointerMove in Building.tsx) */}
      {ghostPosition && (
        <>
          {/* ELEMENT GHOST */}
          {ghostElement && (
            <Element
              element={ghostElement}
              position={ghostPosition}
              index={-999}
              selectedElement={{ selectedElementIndex: -1 }}
              onClick={() => {}}
              opacity={0.6}
              disableInteractions={true}
            />
          )}

          {/* PRODUCT GHOST */}
          {ghostProduct && (
            <Product
              product={ghostProduct as any}
              svgPath={ghostProduct.svgPath}
              position={[ghostPosition.x, ghostPosition.y, 2]}
              rotation={ghostProduct.rotation}
              length={ghostProduct.dimensions.length}
              depth={ghostProduct.dimensions.depth}
              selectedProduct={{ selectedProductIndex: -1 }}
              index={-999}
              onClick={() => {}}
              // Pass styles to make it look ghost-like if your component supports opacity/styles
              fillColor="rgba(200, 200, 200, 0.5)"
              isGhost={true}
            />
          )}

          {/* MODEL2D GHOST */}
          {ghostModel2D && (
            <Model2D
              model={ghostModel2D as any}
              svgPath={ghostModel2D.filePath}
              position={[ghostPosition.x, ghostPosition.y, 2]}
              length={ghostModel2D.dimensions.length}
              depth={ghostModel2D.dimensions.depth}
              rotation={ghostModel2D.rotation}
              index={-999}
              selectedModel2D={{ selectedModel2DIndex: -1 }}
              onClick={() => {}}
              isGhost={true}
            />
          )}
        </>
      )}

      {/* Render Rooms */}
      {(building.rooms ?? []).map((roomModel, i) => (
        <Room key={roomModel.uuid ?? i} roomModel={roomModel} onFloorPointerMove={handlePointerMove} onFloorPointerDown={handlePointerDown} />
      ))}

      {/* Snaplines & Plane */}
      {drawEnabled && snapLines.map((line, idx) => <SnapLine key={idx} {...line} />)}

      <mesh ref={planeRef as Ref<Mesh>} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}>
        <planeGeometry args={[SINGLE_CANVAS_SIDE_SIZE, SINGLE_CANVAS_SIDE_SIZE]} />
        <meshBasicMaterial visible={false} />
      </mesh>
    </group>
  );
};

export default Building;
